import { ChevronLeft } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  Category,
  getRegionBoxMetricData,
  getSalesBreakdown,
} from '@/api/clarity'
import {
  Loading,
  ScalrCarousel,
  TreeTable,
} from '@/components/customComponents'
import { Button } from '@/components/ui/button'

import {
  REGION_CARD_BG_COLORS,
  REGION_CARD_ICONS,
} from '@/constants/METRIC_CARD_ICONS'
import { IAbstractRegionMetric } from '@/types'
import MiniMetricCard from './MiniMetricCard'
import ScalrBoxCard from './ScalrBoxCard'

const itemsDataColumns = [
  {
    header: 'Name',
    accessorKey: 'name',
  },
  {
    header: 'Count',
    accessorKey: 'sale_count',
  },
  {
    header: 'Mix Percentage',
    accessorKey: 'percentage',
    type: 'percentage',
  },
]

interface MetricCardData {
  title: string
  type: 'money' | 'percentage' | 'number'
  leftValue?: string | number
  rightValue?: string | number
}

interface ScalrBoxViewProps<T> {
  data: IAbstractRegionMetric<T>[]
  loading: boolean
  filterValues?: {
    start: string
    end: string
  }
  showKpi?: boolean
}

export function ScalrBoxView<T>({
  data,
  loading,
  filterValues,
  showKpi = true,
}: ScalrBoxViewProps<T>) {
  const { t } = useTranslation()
  const [selectedBox, setSelectedBox] = useState<string | null>(null)
  const [selectedSubBox, setSelectedSubBox] = useState<string | null>(null)
  const [selectedMetric, setSelectedMetric] = useState<string | null>(null)
  const [selectedCardId, setSelectedCardId] = useState<string | null>(null)
  const [itemsBreakDown, setItemsBreakDown] = useState<Category[]>([])
  const [metricCards, setMetricCards] = useState<MetricCardData[] | null>(null)

  const fetchItemsBreakDown = async () => {
    if (!selectedSubBox) return
    try {
      if (!filterValues) {
        console.error('Filter values are not provided')
        return
      }
      const response = await getSalesBreakdown({
        branchName: selectedSubBox,
        startDate: filterValues.start,
        endDate: filterValues.end,
      })
      setItemsBreakDown(response)
    } catch (error) {
      console.error('Error fetching items breakdown:', error)
    }
  }

  const handleBoxClick = (box: string | null) => {
    setSelectedBox(box)
    setSelectedMetric(null)
    setSelectedCardId(null)
  }

  const handleSubBoxClick = (subBox: string | null) => {
    setSelectedSubBox(subBox)
  }

  const handleMetricSelect = (metricValue: string, cardId: string) => {
    setSelectedMetric(metricValue)
    setSelectedCardId(cardId)
  }

  const getMetricData = async (
    metricType: string | null
  ): Promise<MetricCardData[] | null> => {
    if (
      !metricType ||
      !filterValues?.start ||
      !filterValues?.end ||
      !selectedSubBox
    )
      return null
    const metricData = await getRegionBoxMetricData({
      branches: selectedSubBox,
      startDate: filterValues.start,
      endDate: filterValues.end,
    })

    return metricData[metricType] || null
  }

  useEffect(() => {
    setMetricCards(null)
    setItemsBreakDown([])
    if (selectedMetric === 'avgCheck' && selectedSubBox) {
      fetchItemsBreakDown()
    }
    if (selectedMetric && selectedSubBox) {
      getMetricData(selectedMetric).then((data) => {
        setMetricCards(data)
      })
    }

    return () => {
      setMetricCards(null)
      setItemsBreakDown([])
    }
  }, [selectedSubBox, selectedMetric])

  return (
    <div className="space-y-6 !mb-10">
      <div className={`flex items-center`}>
        {(selectedBox || selectedSubBox) && (
          <Button
            variant="outline"
            size="icon"
            onClick={() => {
              setSelectedBox(null)
              setSelectedSubBox(null)
              setSelectedMetric(null)
              setSelectedCardId(null)
            }}
            className="h-6 w-6 mr-2"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
        )}
        <span className="leading-none tracking-tight text-black-500 text-display-s">
          {t('regionMetrics.title')}
        </span>
      </div>

      {loading ? (
        <Loading />
      ) : (
        <div className="gap-4">
          {selectedBox &&
            (() => {
              const item = data.find((item) => item.stand === selectedBox)
              const itemIndex = data.findIndex(
                (item) => item.stand === selectedBox
              )
              return (
                item &&
                item.children && (
                  <ScalrCarousel>
                    {[
                      <ScalrBoxCard
                        key={item.stand}
                        data={item}
                        icon={
                          REGION_CARD_ICONS[
                            itemIndex % REGION_CARD_ICONS.length
                          ]
                        }
                        color={
                          REGION_CARD_BG_COLORS[
                            itemIndex % REGION_CARD_BG_COLORS.length
                          ]
                        }
                        cardOnClick={handleBoxClick}
                        isParent
                        selectedMetric={selectedMetric}
                        selectedCardId={selectedCardId}
                        onMetricSelect={handleMetricSelect}
                        cardId={item.stand}
                        selected={true}
                        showKpi={showKpi}
                      />,
                      ...item.children.map((child) => (
                        <ScalrBoxCard
                          key={child.stand}
                          data={child}
                          icon={
                            REGION_CARD_ICONS[
                              itemIndex % REGION_CARD_ICONS.length
                            ]
                          }
                          color={
                            REGION_CARD_BG_COLORS[
                              itemIndex % REGION_CARD_BG_COLORS.length
                            ]
                          }
                          cardOnClick={handleSubBoxClick}
                          selectedMetric={selectedMetric}
                          selectedCardId={selectedCardId}
                          onMetricSelect={handleMetricSelect}
                          cardId={child.stand}
                          showKpi={showKpi}
                        />
                      )),
                    ]}
                  </ScalrCarousel>
                )
              )
            })()}
          {!selectedBox && (
            <ScalrCarousel>
              {data.map((item, index) => (
                <ScalrBoxCard
                  key={item.stand}
                  data={item}
                  icon={REGION_CARD_ICONS[index % REGION_CARD_ICONS.length]}
                  color={
                    REGION_CARD_BG_COLORS[index % REGION_CARD_BG_COLORS.length]
                  }
                  cardOnClick={handleBoxClick}
                  isParent
                  selectedMetric={selectedMetric}
                  selectedCardId={selectedCardId}
                  onMetricSelect={handleMetricSelect}
                  cardId={item.stand}
                  showKpi={showKpi}
                />
              ))}
            </ScalrCarousel>
          )}
        </div>
      )}

      {/* Display MiniMetricCards when a metric is selected */}
      {selectedMetric && metricCards && (
        <div className="grid grid-cols-5 gap-4 mb-6">
          {metricCards.map((card, index) => (
            <MiniMetricCard
              key={index}
              title={card.title}
              type={card.type}
              leftValue={card.leftValue}
              rightValue={card.rightValue}
            />
          ))}
        </div>
      )}
      {selectedMetric && !metricCards && (
        <div className="flex justify-center items-center h-32">
          <Loading />
        </div>
      )}

      {selectedMetric === 'avgCheck' &&
        (itemsBreakDown.length === 0 ? (
          <div className="flex justify-center items-center h-32">
            <Loading />
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-4">
            <TreeTable
              data={itemsBreakDown}
              columns={itemsDataColumns}
              isCard={false}
              height={30}
            />
          </div>
        ))}
    </div>
  )
}
